import React, {useCallback, useEffect, useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Backdrop,
    Box,
    CircularProgress,
    Drawer,
    IconButton,
    Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import {getFilters, getMultipliersDetails} from "./api/getMultipliersDetails";
import {multipliersMenu, multipliersToApi} from "./constant/multipliersMenu";
import "./ghaBidsViewer.scss";
import HotelGroupMarketSelector from "./component/HotelGroupMarketSelector";
import MultipliersTabs from "./component/MultipliersTabs";
import MultipliersTable from "./component/MultipliersTable";
import {titleBidOverMultiplier} from "./api/title";
import {deleteMultipliers} from "./api/deleteMultipliers";
import { useSnackbar } from 'notistack';

const GhaBidsViewer = ({ isOpen, onClose, idAccount, platform }) => {
    const [data, setData] = useState({
        "groups_list": [],
        "hotels_list": [],
        "markets_list": [],
        "can_use_group": false,
        "groups_multipliers": {
            "device_multipliers":  [],
            "need_date_multipliers": [],
            "length_of_stay_multipliers": [],
            "check_in_day_multipliers": [],
            "days_to_arrival_multipliers": [],
            "date_type_multipliers": [],
        },
        "hotels_multipliers": {
            "device_multipliers": [],
            "need_date_multipliers": [],
            "length_of_stay_multipliers": [],
            "check_in_day_multipliers": [],
            "days_to_arrival_multipliers": [],
            "date_type_multipliers": [],
        }
    });
    const [selectedTab, setSelectedTab] = useState(multipliersMenu[0]?.id);
    const [expanded, setExpanded] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    // Hotels Selector
    const [selectedItem, setSelectedItem] = useState([]);
    const [selectedType, setSelectedType] = useState("hotels");
    const [selectedCategory, setSelectedCategory] = useState("hotels");

    // Market Selector
    const [selectedMarkets, setSelectedMarkets] = useState([]);

    // Pagination states
    const [page, setPage] = useState(0);

    // Loader state
    const [loading, setLoading] = useState({
        filter: false,
        multipliersMenu: {
            device_multipliers: false,
            need_date_multipliers: false,
            length_of_stay_multipliers: false,
            check_in_day_multipliers: false,
            days_to_arrival_multipliers: false,
            date_type_multipliers: false
        }
    });

    // SnackBar
    const { enqueueSnackbar } = useSnackbar();

    // Select Rows in the table
    const [selectedRows, setSelectedRows] = useState([]);

    const fetchMultipliersData = useCallback(async () => {
        setLoading(prev => ({
            ...prev,
            filter: true,
            multipliersMenu: {
                device_multipliers: true,
                need_date_multipliers: true,
                length_of_stay_multipliers: true,
                check_in_day_multipliers: true,
                days_to_arrival_multipliers: true,
                date_type_multipliers: true
            }
        }));

        const updateData = (category, type, result) => {
            setData(prev => ({
                ...prev,
                [type]: {
                    ...prev[type],
                    [category]: result,
                }
            }));

            setLoading(prev => ({
                ...prev,
                multipliersMenu: {
                    ...prev.multipliersMenu,
                    [category]: false
                }
            }));
        };

        try {
            getMultipliersDetails(idAccount, platform, "device").then(res => {
                updateData("device_multipliers", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                updateData("device_multipliers", "groups_multipliers", res.data?.groups_multipliers ?? []);
            });

            getMultipliersDetails(idAccount, platform, "los").then(res => {
                updateData("length_of_stay_multipliers", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                updateData("length_of_stay_multipliers", "groups_multipliers", res.data?.groups_multipliers ?? []);
            });

            getMultipliersDetails(idAccount, platform, "check_in_day").then(res => {
                updateData("check_in_day_multipliers", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                updateData("check_in_day_multipliers", "groups_multipliers", res.data?.groups_multipliers ?? []);
            });

            getMultipliersDetails(idAccount, platform, "days_to_arrival").then(res => {
                updateData("days_to_arrival_multipliers", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                updateData("days_to_arrival_multipliers", "groups_multipliers", res.data?.groups_multipliers ?? []);
            });

            getMultipliersDetails(idAccount, platform, "date_type").then(res => {
                updateData("date_type_multipliers", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                updateData("date_type_multipliers", "groups_multipliers", res.data?.groups_multipliers ?? []);
            });

            getMultipliersDetails(idAccount, platform, "need_date").then(res => {
                updateData("need_date_multipliers", "hotels_multipliers", res.data?.hotels_multipliers ?? []);
                updateData("need_date_multipliers", "groups_multipliers", res.data?.groups_multipliers ?? []);
            });

            // Filters
            const filtersResult = await getFilters(idAccount, platform);
            setData(prev => ({
                ...prev,
                groups_list: filtersResult.data?.groups_list ?? [],
                hotels_list: filtersResult.data?.hotels_list ?? [],
                markets_list: filtersResult.data?.markets_list ?? [],
                can_use_group: filtersResult.data?.can_use_group ?? false
            }));
            setLoading(prev => ({ ...prev, filter: false }));
        } catch (error) {
            enqueueSnackbar('Error while fetching multipliers:', { variant: 'error' });
            setLoading({
                filter: false,
                multipliersMenu: {
                    device_multipliers: false,
                    need_date_multipliers: false,
                    length_of_stay_multipliers: false,
                    check_in_day_multipliers: false,
                    days_to_arrival_multipliers: false,
                    date_type_multipliers: false
                }
            });
        }
    }, [idAccount, platform]);

    useEffect(() => {
        if (isOpen) {
            fetchMultipliersData();
        }
    }, [isOpen, fetchMultipliersData]);

    const handleTabChange = (_, newValue) => {
        setSelectedTab(newValue);
        setSelectedRows([]);
        setPage(0);
    };

    const handleAccordionToggle = () => setExpanded(!expanded);
    const handleChangePage = (_, newPage) => setPage(newPage);

    const handleDelete = async (bidOrBids) => {
        const bids = Array.isArray(bidOrBids) ? bidOrBids : [bidOrBids];

        setLoading(prev => ({
            ...prev,
            multipliersMenu: {
                ...prev.multipliersMenu,
                [selectedTab]: true
            }
        }));

        try {
            const result = await deleteMultipliers(bids, platform, idAccount, selectedType);

            if (result.success) {
                const response = await getMultipliersDetails(idAccount, platform, multipliersToApi[selectedTab]);
                setData(prevData => {
                    if (selectedType === "hotels") {
                        return {
                            ...prevData,
                            hotels_multipliers: {
                                ...prevData.hotels_multipliers,
                                [selectedTab]: response.data?.hotels_multipliers ?? []
                            }
                        };
                    } else {
                        return {
                            ...prevData,
                            groups_multipliers: {
                                ...prevData.groups_multipliers,
                                [selectedTab]: response.data?.groups_multipliers ?? []
                            }
                        };
                    }
                });
                enqueueSnackbar('Multiplier(s) deleted successfully.', { variant: 'success' });
            } else {
                enqueueSnackbar('Failed to delete multiplier(s).', { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Error deleting multiplier(s).', { variant: 'error' });
        } finally {
            setLoading(prev => ({
                ...prev,
                multipliersMenu: {
                    ...prev.multipliersMenu,
                    [selectedTab]: false
                }
            }));
        }
    };

    // Determine correct multipliers data based on selection
    const multipliersData = selectedCategory === "hotels"
        ? data.hotels_multipliers[selectedTab]
        : data.groups_multipliers[selectedTab];

    const filteredMultipliers = multipliersData?.filter(item => {
        const isMarketMatch =
            selectedMarkets.length === 0 ||
            selectedMarkets.some(market => market.market_id === item.market_id);

        const isItemMatch =
            selectedItem.length === 0 ||
            selectedItem.some(selItem => {
                if (!selItem?.id) return false;

                const selId = selItem.id.toString();
                const entityId = item.entity_id?.toString();

                return entityId === selId;
            });

        return isMarketMatch && isItemMatch;
    }) || [];

    const updateMultiplierValue = (editPath, newValue) => {
        setData((prevData) => {
            const key = selectedType === "hotels" ? "hotels_multipliers" : "groups_multipliers";
            const updatedMultipliers = prevData[key][selectedTab]?.map((item) =>
                item.bid_value_edit_path === editPath
                    ? { ...item, multiplier_value: newValue }
                    : item
            ) || [];

            return {
                ...prevData,
                [key]: {
                    ...prevData[key],
                    [selectedTab]: updatedMultipliers
                }
            };
        });
    };

    return (
        <Drawer anchor="right" open={isOpen} onClose={onClose}>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={loading.filter}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <Box className={`ghaBidsViewerPanel ${isOpen ? "open" : ""}`} style={{ padding: 16, width: "800px" }}>
                {/* Header */}
                <Box className="header">
                    <Box style={{ display: "flex", alignItems: "center" }}>
                        <IconButton onClick={onClose} className="closeButton">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body1" className="title">Multipliers - {titleBidOverMultiplier[platform]}</Typography>
                    </Box>
                </Box>

                {/* Accordion */}
                <Accordion expanded={expanded} onChange={handleAccordionToggle} className="multipliers-accordion">
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} className="accordion-summary">
                        <Typography fontWeight="bold">Active Multipliers</Typography>
                    </AccordionSummary>
                    <AccordionDetails className="accordion-content">
                        <HotelGroupMarketSelector
                            data={data}
                            setSelectedCategory={setSelectedCategory}
                            selectedItem={selectedItem}
                            setSelectedItem={setSelectedItem}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            selectedMarkets={selectedMarkets}
                            setSelectedMarkets={setSelectedMarkets}
                            selectedType={selectedType}
                            setSelectedType={setSelectedType}
                            setPage={setPage}
                        />

                        {/* Tabs */}
                        <MultipliersTabs selectedTab={selectedTab} handleTabChange={handleTabChange} />

                        {/* Table */}
                        <MultipliersTable
                            multipliers={filteredMultipliers}
                            page={page}
                            onPageChange={handleChangePage}
                            handleDelete={handleDelete}
                            platform={platform}
                            hotelOrGroupType={selectedType}
                            onUpdateMultiplierValue={updateMultiplierValue}
                            selectedRows={selectedRows}
                            setSelectedRows={setSelectedRows}
                            loading={loading}
                            selectedTab={selectedTab}
                        />
                    </AccordionDetails>
                </Accordion>
            </Box>
        </Drawer>
    );
};

export default GhaBidsViewer;
